import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  BarChart3,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Download,
  CheckCircle,
  AlertCircle,
  Calendar,
  ArrowRight,
  Mail,
  Info,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import Sparkline from "../components/Sparkline";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const PROVINCE_COLORS = {
  "Gauteng": "#16a34a", "KwaZulu-Natal": "#2563eb", "Free State": "#16a34a",
  "Eastern Cape": "#9333ea", "Mpumalanga": "#dc2626", "Limpopo": "#ca8a04",
  "North West": "#0891b2", "Northern Cape": "#be185d", "Western Cape": "#059669",
};

// Maps each province to its highest-rate city (matches PROVINCE_MAP in shipping_sync.py)
const PROVINCE_PRIMARY_DEST = {
  "Gauteng": "Pretoria",
  "KwaZulu-Natal": "Pietermaritzburg",
  "Free State": "Welkom",
  "Eastern Cape": "East London",
  "Mpumalanga": "Nelspruit",
  "Limpopo": "Polokwane",
  "North West": "Rustenburg",
  "Northern Cape": "Upington",
  "Western Cape": "Stellenbosch",
};

// All cities per province — used for averaging when province is set as "local"
const PROVINCE_CITIES = {
  "Gauteng": ["Johannesburg", "Pretoria"],
  "KwaZulu-Natal": ["Durban", "Pietermaritzburg"],
  "Free State": ["Bloemfontein", "Welkom"],
  "Eastern Cape": ["East London", "Port Elizabeth"],
  "Mpumalanga": ["Nelspruit", "Witbank"],
  "Limpopo": ["Polokwane"],
  "North West": ["Rustenburg"],
  "Northern Cape": ["Kimberley", "Upington"],
  "Western Cape": ["Cape Town", "Stellenbosch", "George"],
};

const DEST_COLORS = {
  Johannesburg: "#16a34a", Pretoria: "#ea580c",
  Durban: "#2563eb", Pietermaritzburg: "#3b82f6",
  Bloemfontein: "#16a34a", Welkom: "#15803d",
  "East London": "#7c3aed", "Port Elizabeth": "#a855f7",
  Nelspruit: "#dc2626", Witbank: "#ef4444",
  Polokwane: "#ca8a04", Rustenburg: "#0891b2",
  Kimberley: "#be185d", Upington: "#e11d48",
  "Cape Town": "#047857", Stellenbosch: "#10b981", George: "#059669",
};

// Same 25 weight tiers as Shipping Sync
const WEIGHT_TIERS = [
  { min: 0, max: 5, ffWeight: 5, label: "0-5kg" },
  { min: 5, max: 10, ffWeight: 10, label: "5-10kg" },
  { min: 10, max: 20, ffWeight: 20, label: "10-20kg" },
  { min: 20, max: 30, ffWeight: 30, label: "20-30kg" },
  { min: 30, max: 40, ffWeight: 40, label: "30-40kg" },
  { min: 40, max: 50, ffWeight: 50, label: "40-50kg" },
  { min: 50, max: 70, ffWeight: 70, label: "50-70kg" },
  { min: 70, max: 90, ffWeight: 90, label: "70-90kg" },
  { min: 90, max: 120, ffWeight: 120, label: "90-120kg" },
  { min: 120, max: 150, ffWeight: 150, label: "120-150kg" },
  { min: 150, max: 200, ffWeight: 200, label: "150-200kg" },
  { min: 200, max: 250, ffWeight: 250, label: "200-250kg" },
  { min: 250, max: 300, ffWeight: 300, label: "250-300kg" },
  { min: 300, max: 350, ffWeight: 350, label: "300-350kg" },
  { min: 350, max: 400, ffWeight: 400, label: "350-400kg" },
  { min: 400, max: 450, ffWeight: 450, label: "400-450kg" },
  { min: 450, max: 500, ffWeight: 500, label: "450-500kg" },
  { min: 500, max: 550, ffWeight: 550, label: "500-550kg" },
  { min: 550, max: 600, ffWeight: 600, label: "550-600kg" },
  { min: 600, max: 650, ffWeight: 650, label: "600-650kg" },
  { min: 650, max: 700, ffWeight: 700, label: "650-700kg" },
  { min: 700, max: 750, ffWeight: 750, label: "700-750kg" },
  { min: 750, max: 800, ffWeight: 800, label: "750-800kg" },
  { min: 800, max: 900, ffWeight: 900, label: "800-900kg" },
  { min: 900, max: 1000, ffWeight: 1000, label: "900-1000kg" },
  { min: 1000, max: 1500, ffWeight: 1500, label: "1000-1500kg" },
  { min: 1500, max: 2000, ffWeight: 2000, label: "1500-2000kg" },
  { min: 2000, max: 3000, ffWeight: 3000, label: "2000-3000kg" },
  { min: 3000, max: 4000, ffWeight: 4000, label: "3000-4000kg" },
  { min: 4000, max: 5000, ffWeight: 5000, label: "4000-5000kg" },
];

const PricingPage = () => {
  const [currentRates, setCurrentRates] = useState(null);
  const [rateHistory, setRateHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedWeight, setSelectedWeight] = useState("150");
  const [notifications, setNotifications] = useState([]);
  const [schedulerInfo, setSchedulerInfo] = useState(null);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [wcDiscountLight, setWcDiscountLight] = useState(0);
  const [wcDiscountHeavy, setWcDiscountHeavy] = useState(0);
  const [localProvince, setLocalProvince] = useState("Western Cape");
  const fetchDataRef = useRef(null);

  const startPolling = useCallback(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/pricing/refresh-status`);
        const s = res.data;
        setRefreshProgress(s);
        if (!s.running) {
          clearInterval(interval);
          setChecking(false);
          if (s.error) {
            toast.error(`Rate check failed: ${s.error}`);
            setRefreshProgress(null);
          } else if (s.rates_count !== undefined) {
            const v = s.verification;
            if (v?.complete) {
              toast.success(`${v.found}/${v.expected} rates imported — 100% verified`);
            } else if (v) {
              toast.warning(`${v.found}/${v.expected} rates imported (${v.coverage}%) — ${v.missing_count} missing`);
            } else {
              toast.success(`${s.rates_count} rates fetched`);
            }
            if (fetchDataRef.current) fetchDataRef.current();
          }
        }
      } catch {
        clearInterval(interval);
        setChecking(false);
        setRefreshProgress(null);
      }
    }, 2000);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ratesRes, historyRes, notifsRes, schedRes, statusRes, discountRes] = await Promise.all([
        axios.get(`${API}/pricing/rates`),
        axios.get(`${API}/pricing/history`),
        axios.get(`${API}/pricing/notifications`),
        axios.get(`${API}/scheduler/status`).catch(() => ({ data: null })),
        axios.get(`${API}/pricing/refresh-status`).catch(() => ({ data: null })),
        axios.get(`${API}/pricing/discount`).catch(() => ({ data: { discount_percent: 0 } })),
      ]);
      if (ratesRes.data.success) setCurrentRates(ratesRes.data.data);
      setRateHistory(historyRes.data || []);
      setNotifications(notifsRes.data.notifications || []);
      if (discountRes.data) {
        setWcDiscountLight(discountRes.data.wc_discount_light || 0);
        setWcDiscountHeavy(discountRes.data.wc_discount_heavy || 0);
        setLocalProvince(discountRes.data.local_province || "Western Cape");
      }
      if (schedRes.data) setSchedulerInfo(schedRes.data);
      if (statusRes.data?.running) {
        setChecking(true);
        setRefreshProgress(statusRes.data);
        startPolling();
      } else if (statusRes.data?.verification) {
        // Show last verification result on page load
        setRefreshProgress(statusRes.data);
      }
    } catch (error) {
      console.error("Error fetching pricing data:", error);
    } finally {
      setLoading(false);
    }
  }, [startPolling]);

  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const checkPrices = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/pricing/refresh`);
      if (res.data.already_running) {
        toast.info("Rate check already in progress");
      } else {
        toast.success("Rate check started — fetching 350 quotes...");
      }
      startPolling();
    } catch (err) {
      toast.error("Failed to start rate check");
      setChecking(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/pricing/pdf`, { responseType: "blob", timeout: 30000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `rate-card-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rate card downloaded!");
    } catch (err) {
      toast.error(err.response?.status === 404 ? "No rates stored yet" : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const saveDiscount = async (light, heavy, province) => {
    try {
      await axios.post(`${API}/pricing/discount`, {
        wc_discount_light: light,
        wc_discount_heavy: heavy,
        local_province: province || localProvince,
      });
    } catch {
      toast.error("Failed to save discount");
    }
  };

  const syncToShopify = async () => {
    setSyncing(true);
    const hasDiscount = wcDiscountLight > 0 || wcDiscountHeavy > 0;
    const toastId = toast.loading(hasDiscount ? "Syncing FF rates to Shopify (WC discounted)..." : "Syncing FF rates to Shopify...");
    try {
      const res = await axios.post(`${API}/shipping/sync`, {}, { timeout: 60000 });
      if (res.data.success) {
        const action = res.data.action === "created" ? "Created" : "Updated";
        const zones = res.data.zones_created || res.data.zones_updated || [];
        toast.success(`${action} Shopify profile with ${zones.length} zones × 30 weight tiers`, { id: toastId });
      } else {
        toast.error(res.data.error || "Sync failed", { id: toastId });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 || detail.toLowerCase().includes("scope") || detail.toLowerCase().includes("permission")) {
        toast.error("Shopify shipping scopes not authorized. Reconnect in Settings.", { id: toastId });
      } else {
        toast.error(detail || "Sync failed", { id: toastId });
      }
    } finally {
      setSyncing(false);
    }
  };

  // Build rate lookup: dest -> weight -> rate detail (using preferred service per destination)
  const rateLookup = {};
  if (currentRates?.rates) {
    currentRates.rates.forEach((r) => {
      const isPreferred = r.preferred !== undefined ? r.preferred : r.service === "ECO";
      if (!isPreferred) return;
      const key = `${r.destination}_${r.weight}`;
      rateLookup[key] = r;
    });
  }

  // For each tier, find the rate for a destination
  // If exact ff_weight match not found, find closest available
  const getRateForTier = (dest, tier) => {
    const exact = rateLookup[`${dest}_${tier.ffWeight}`];
    if (exact) return { ...exact, matched: true };

    // Find all rates for this destination sorted by weight
    const destRates = currentRates?.rates
      ?.filter(r => r.destination === dest && (r.preferred !== undefined ? r.preferred : r.service === "ECO"))
      ?.sort((a, b) => a.weight - b.weight) || [];

    if (destRates.length === 0) return null;

    // Find closest higher weight
    const higher = destRates.find(r => r.weight >= tier.ffWeight);
    if (higher) return { ...higher, matched: false, usedWeight: higher.weight };

    // Extrapolate from highest available
    const base = destRates[destRates.length - 1];
    if (base.weight > 0) {
      const perKgTotal = base.total / base.weight;
      const perKgFee = base.shipping_fee / base.weight;
      return {
        ...base,
        total: Math.round(perKgTotal * tier.ffWeight * 100) / 100,
        shipping_fee: Math.round(perKgFee * tier.ffWeight * 100) / 100,
        weight: tier.ffWeight,
        matched: false,
        extrapolated: true,
        usedWeight: base.weight,
      };
    }
    return null;
  };

  // For the local province, average ALL cities in that province
  // For other provinces, use the primary (highest-cost) destination
  const getProvinceRate = (province, tier) => {
    if (province === localProvince) {
      const cities = PROVINCE_CITIES[province] || [];
      const cityRates = cities.map(city => getRateForTier(city, tier)).filter(Boolean);
      if (cityRates.length === 0) return null;
      if (cityRates.length === 1) return { ...cityRates[0], matched: true };
      const avg = (vals) => Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 100) / 100;
      return {
        total: avg(cityRates.map(r => r.total)),
        shipping_fee: avg(cityRates.map(r => r.shipping_fee)),
        cartage: avg(cityRates.map(r => r.cartage || 0)),
        docs: avg(cityRates.map(r => r.docs || 0)),
        surcharge: avg(cityRates.map(r => r.surcharge || 0)),
        outly: avg(cityRates.map(r => r.outly || 0)),
        insurance: avg(cityRates.map(r => r.insurance || 0)),
        vat: avg(cityRates.map(r => r.vat || 0)),
        baseline_total: cityRates.every(r => r.baseline_total != null)
          ? avg(cityRates.map(r => r.baseline_total)) : undefined,
        weight: tier.ffWeight,
        matched: true,
        averaged: cityRates.length > 1,
        city_details: cityRates.map(r => ({ name: r.destination || cities[cityRates.indexOf(r)], total: r.total })),
      };
    }
    const dest = PROVINCE_PRIMARY_DEST[province];
    return dest ? getRateForTier(dest, tier) : null;
  };

  const allWeights = currentRates?.rates
    ? [...new Set(currentRates.rates.map(r => r.weight))].sort((a, b) => a - b)
    : [];

  // Chart data
  const chartData = rateHistory
    .filter((h) => h.weight === parseInt(selectedWeight))
    .sort((a, b) => a.checked_at.localeCompare(b.checked_at));

  // Per-route price trend series (for the inline sparklines), at the selected weight tier.
  const getTrendSeries = (dest) => rateHistory
    .filter((h) => h.weight === parseInt(selectedWeight) && typeof h[dest] === "number")
    .sort((a, b) => (a.checked_at || "").localeCompare(b.checked_at || ""))
    .map((h) => h[dest]);

  const destinations = currentRates?.rates
    ? [...new Set(currentRates.rates.filter(r => r.preferred !== undefined ? r.preferred : r.service === "ECO").map(r => r.destination))]
    : [];

  // Biggest Movers: top routes that ROSE most this calendar month (at the selected weight).
  const biggestMovers = (() => {
    const w = parseInt(selectedWeight);
    const snaps = rateHistory
      .filter((h) => h.weight === w)
      .sort((a, b) => (a.checked_at || "").localeCompare(b.checked_at || ""));
    if (snaps.length < 2) return { hasHistory: false, movers: [] };

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const latest = snaps[snaps.length - 1];
    const movers = [];
    destinations.forEach((dest) => {
      const cur = latest[dest];
      if (typeof cur !== "number") return;
      // Baseline = last snapshot BEFORE this month, else the first snapshot within this month.
      const before = [...snaps].reverse().find((s) => s.checked_at < monthStart && typeof s[dest] === "number");
      let baseline = before ? before[dest] : undefined;
      if (typeof baseline !== "number") {
        const firstInMonth = snaps.find((s) => s.checked_at >= monthStart && typeof s[dest] === "number");
        baseline = firstInMonth ? firstInMonth[dest] : undefined;
      }
      if (typeof baseline !== "number" || baseline <= 0) return;
      const delta = cur - baseline;
      if (delta > 0.005) movers.push({ dest, from: baseline, to: cur, delta, pct: (delta / baseline) * 100 });
    });
    movers.sort((a, b) => b.delta - a.delta);
    return { hasHistory: true, movers: movers.slice(0, 3) };
  })();

  const formatShortDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short" });
  };

  // Simple cell showing just a raw rate value (no tooltip, no discount)
  const renderRawCell = (rate, tier) => {
    if (!rate) return <td key={tier.label} className="px-2 py-2 text-center text-slate-300 text-[10px]">—</td>;
    return (
      <td key={tier.label} className="px-2 py-2 text-center">
        <span className="font-mono text-[10px] text-slate-500">R{rate.total.toFixed(0)}</span>
      </td>
    );
  };

  const renderTierCell = (label, tier, precomputedRate) => {
    const rate = precomputedRate !== undefined ? precomputedRate : getRateForTier(label, tier);
    if (!rate) return <td key={tier.label} className="px-2 py-2 text-center text-slate-300 text-[10px]">—</td>;

    const isLocal = label === localProvince;
    const isLight = tier.ffWeight <= 30;
    const localDiscount = isLight ? wcDiscountLight : wcDiscountHeavy;
    const df = isLocal && localDiscount > 0 ? 1 - localDiscount / 100 : 1;
    const rawTotal = rate.total;
    const total = Math.round(rawTotal * df * 100) / 100;
    const fee = rate.shipping_fee;
    const rawBaseline = rate.baseline_total;
    const baselineTotal = rawBaseline !== undefined && rawBaseline !== null ? Math.round(rawBaseline * df * 100) / 100 : undefined;
    const hasBaseline = baselineTotal !== undefined && baselineTotal !== null;
    const baselineChanged = hasBaseline && Math.abs(total - baselineTotal) > 0.01;
    const pct = baselineChanged ? ((total - baselineTotal) / baselineTotal) * 100 : 0;

    return (
      <td key={tier.label} className="px-2 py-2 text-center">
        <TooltipProvider delayDuration={200}>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className={`cursor-help inline-block ${rate.extrapolated ? "italic" : ""}`}>
                <span className={`font-mono text-[10px] font-medium ${rate.extrapolated ? "text-amber-700" : "text-slate-800"}`}>
                  R{total.toFixed(0)}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-slate-900 text-white border-slate-700 p-3 max-w-[260px]">
              <p className="font-medium text-xs mb-2 text-slate-300">{label} — {tier.label}</p>
              <div className="space-y-1 text-xs">
                {hasBaseline && (
                  <div className="flex justify-between gap-4">
                    <span className="text-blue-400">Original Total</span>
                    <span>R{baselineTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <span className="text-green-400 font-medium">{isLocal && localDiscount > 0 ? "Customer Price" : "Total Incl. VAT"}</span>
                  <span className="font-medium">R{total.toFixed(2)}</span>
                </div>
                {isLocal && localDiscount > 0 && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400">FF Cost (before -{localDiscount}%)</span>
                    <span className="text-slate-400">R{rawTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-slate-700 pt-1 mt-1 space-y-0.5 text-[11px]">
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-400">Shipping Fee</span>
                    <span>R{fee.toFixed(2)}</span>
                  </div>
                  {rate.cartage !== undefined && (
                    <div className="pl-2 space-y-0.5 text-[10px] text-slate-500">
                      <div className="flex justify-between gap-4"><span>Cartage</span><span>R{rate.cartage.toFixed(2)}</span></div>
                      <div className="flex justify-between gap-4"><span>Docs</span><span>R{rate.docs.toFixed(2)}</span></div>
                      <div className="flex justify-between gap-4"><span>Surcharge</span><span>R{rate.surcharge.toFixed(2)}</span></div>
                    </div>
                  )}
                  {rate.outly > 0 && (
                    <div className="flex justify-between gap-4"><span className="text-amber-400">+ Outlying</span><span>R{rate.outly.toFixed(2)}</span></div>
                  )}
                  {rate.insurance > 0 && (
                    <div className="flex justify-between gap-4"><span className="text-blue-400">+ Insurance</span><span>R{rate.insurance.toFixed(2)}</span></div>
                  )}
                  {rate.vat > 0 && (
                    <div className="flex justify-between gap-4"><span className="text-slate-400">+ VAT (15%)</span><span>R{rate.vat.toFixed(2)}</span></div>
                  )}
                </div>
                {baselineChanged && (
                  <div className="flex justify-between gap-4 border-t border-slate-700 pt-1 mt-1">
                    <span className={pct > 0 ? "text-red-400" : "text-green-400"}>Change</span>
                    <span className={pct > 0 ? "text-red-400 font-medium" : "text-green-400 font-medium"}>
                      {pct > 0 ? "+" : ""}R{(total - baselineTotal).toFixed(2)} ({pct > 0 ? "+" : ""}{pct.toFixed(1)}%)
                    </span>
                  </div>
                )}
                {rate.extrapolated && (
                  <p className="text-amber-400 text-[10px] mt-1 border-t border-slate-700 pt-1">Extrapolated from {rate.usedWeight}kg rate</p>
                )}
                {rate.averaged && rate.city_details && (
                  <p className="text-amber-500 text-[10px] mt-1 border-t border-slate-700 pt-1">
                    Avg: {rate.city_details.map(c => `${c.name} R${c.total?.toFixed(0)}`).join(" + ")}
                  </p>
                )}
                {!rate.matched && !rate.extrapolated && rate.usedWeight && (
                  <p className="text-slate-500 text-[10px] mt-1 border-t border-slate-700 pt-1">Using {rate.usedWeight}kg FF quote</p>
                )}
                <div className="text-[10px] text-slate-600 mt-1">FF quote weight: {rate.weight}kg</div>
              </div>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </td>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900" data-testid="pricing-title">Pricing</h1>
          <p className="text-sm text-slate-500 mt-1">
            Rate overview — all provinces
            {schedulerInfo?.scheduler_running && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-600">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Auto-check daily 07:00
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 border-2 border-[#16a34a]/30 rounded-md px-2 py-1 bg-orange-50/30" data-testid="local-province-controls">
            <Select value={localProvince} onValueChange={(val) => { setLocalProvince(val); saveDiscount(wcDiscountLight, wcDiscountHeavy, val); }}>
              <SelectTrigger className="w-[130px] h-7 text-[11px] border-0 bg-transparent shadow-none focus:ring-0 px-1 font-medium text-[#16a34a]" data-testid="local-province-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(PROVINCE_PRIMARY_DEST).sort().map(p => (
                  <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-slate-300">|</span>
            <label className="text-[10px] text-slate-500 whitespace-nowrap">0-30kg</label>
            <input
              type="number"
              min="0"
              max="99"
              value={wcDiscountLight}
              onChange={(e) => setWcDiscountLight(Math.max(0, Math.min(99, Number(e.target.value) || 0)))}
              onBlur={() => saveDiscount(wcDiscountLight, wcDiscountHeavy)}
              className="w-10 text-sm text-center font-mono border-0 bg-transparent focus:outline-none focus:ring-0 text-slate-800"
              data-testid="discount-light-input"
            />
            <span className="text-[10px] text-slate-400 mr-1">%</span>
            <span className="text-slate-300">|</span>
            <label className="text-[10px] text-slate-500 whitespace-nowrap ml-1">30kg+</label>
            <input
              type="number"
              min="0"
              max="99"
              value={wcDiscountHeavy}
              onChange={(e) => setWcDiscountHeavy(Math.max(0, Math.min(99, Number(e.target.value) || 0)))}
              onBlur={() => saveDiscount(wcDiscountLight, wcDiscountHeavy)}
              className="w-10 text-sm text-center font-mono border-0 bg-transparent focus:outline-none focus:ring-0 text-slate-800"
              data-testid="discount-heavy-input"
            />
            <span className="text-[10px] text-slate-400">%</span>
          </div>
          <Button onClick={syncToShopify} disabled={syncing || !currentRates} variant="outline" size="sm" className="border-green-200 text-green-700 hover:bg-green-50" data-testid="sync-to-shopify-btn">
            {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</> : <><Upload className="w-4 h-4 mr-2" />Sync to Shopify</>}
          </Button>
          <Button onClick={downloadPDF} disabled={downloading || !currentRates} variant="outline" size="sm" className="border-slate-200" data-testid="download-ratecard-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Rate Card PDF
          </Button>
          <Button onClick={checkPrices} disabled={checking} className="bg-[#16a34a] hover:bg-[#15803d] text-white" size="sm" data-testid="check-prices-btn">
            {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking...</> : <><RefreshCw className="w-4 h-4 mr-2" />Check Prices</>}
          </Button>
        </div>
      </div>

      {/* Progress Bar & Verification */}
      {refreshProgress && (refreshProgress.running || refreshProgress.verification) && (
        <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="refresh-progress">
          {refreshProgress.running ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#16a34a]" />
                  <span className="text-sm font-medium text-slate-700">Fetching rates from courier...</span>
                </div>
                <span className="text-xs text-slate-500 font-mono">{refreshProgress.progress}/{refreshProgress.total}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div className="bg-[#16a34a] h-2.5 rounded-full transition-all duration-500" style={{ width: `${refreshProgress.total > 0 ? (refreshProgress.progress / refreshProgress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">
                {refreshProgress.current_dest && `${refreshProgress.current_dest} — ${refreshProgress.current_weight}kg`}
              </p>
            </>
          ) : refreshProgress.verification && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {refreshProgress.verification.complete ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                  )}
                  <span className={`text-sm font-semibold ${refreshProgress.verification.complete ? "text-green-700" : "text-amber-700"}`}>
                    Import {refreshProgress.verification.complete ? "Verified" : "Incomplete"} — {refreshProgress.verification.coverage}%
                  </span>
                </div>
                <span className="text-xs text-slate-500 font-mono">
                  {refreshProgress.verification.found}/{refreshProgress.verification.expected} rates
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 mt-2 overflow-hidden">
                <div className={`h-2 rounded-full transition-all duration-500 ${refreshProgress.verification.complete ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${refreshProgress.verification.coverage}%` }} />
              </div>
              {refreshProgress.verification.missing_count > 0 && (
                <div className="mt-2 text-xs text-amber-600">
                  <span className="font-medium">{refreshProgress.verification.missing_count} missing:</span>{" "}
                  {refreshProgress.verification.missing.slice(0, 8).join(", ")}
                  {refreshProgress.verification.missing_count > 8 && ` (+${refreshProgress.verification.missing_count - 8} more)`}
                </div>
              )}
              {refreshProgress.changes_count > 0 && (
                <p className="text-xs text-slate-500 mt-1">{refreshProgress.changes_count} rate change(s) detected</p>
              )}
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#16a34a]" /></div>
      ) : (
        <>
          {/* Biggest Movers — top routes that rose most this month */}
          {biggestMovers.hasHistory && (
            <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid="biggest-movers-panel">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-semibold text-slate-900">Biggest Movers</h3>
                <span className="text-[11px] text-slate-400">top routes that rose most this month · @ {selectedWeight}kg</span>
              </div>
              {biggestMovers.movers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {biggestMovers.movers.map((m, i) => (
                    <div key={m.dest} className="flex items-center justify-between rounded-md border border-red-100 bg-red-50/50 px-3 py-2" data-testid={`biggest-mover-${i}`}>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold text-red-500">#{i + 1}</span>
                          <span className="text-sm font-medium text-slate-900">{m.dest}</span>
                        </div>
                        <span className="text-[11px] text-slate-500">R{m.from.toFixed(2)} → R{m.to.toFixed(2)}</span>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 justify-end text-red-600 font-semibold text-sm">
                          <TrendingUp className="w-3.5 h-3.5" /> +R{m.delta.toFixed(2)}
                        </div>
                        <span className="text-[11px] text-red-500 font-medium">+{m.pct.toFixed(1)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500" data-testid="biggest-movers-empty">No routes rose this month — prices held steady 🎉</p>
              )}
            </div>
          )}

          {/* Notifications */}
          {notifications.length > 0 && notifications[0].changes?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="price-change-banner">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-800 text-sm">
                  {notifications[0].changes.length} rate change(s) detected
                </span>
                {notifications[0].previous_date && (
                  <span className="text-xs text-amber-600 ml-1">(since {formatShortDate(notifications[0].previous_date)})</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-1">
                {notifications[0].changes.map((c, i) => {
                  const changePct = c.old_price ? ((c.change / c.old_price) * 100) : 0;
                  return (
                    <div key={`${c.destination}-${c.weight}-${i}`} className="flex items-center justify-between text-xs bg-white rounded px-3 py-1.5 border border-amber-100">
                      <span className="text-slate-600">{c.destination} {c.weight}kg</span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-slate-400">R{c.old_price?.toFixed(2)}</span>
                        <ArrowRight className="w-2.5 h-2.5 text-slate-400" />
                        <span className={`font-medium ${c.change > 0 ? "text-red-600" : "text-green-600"}`}>R{c.new_price?.toFixed(2)}</span>
                        <span className={`text-[10px] ${c.change > 0 ? "text-red-500" : "text-green-500"}`}>
                          ({changePct > 0 ? "+" : ""}{changePct.toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rates Table — 25 Weight Tiers */}
          <Card className="border-slate-200 shadow-sm mb-6" data-testid="current-rates-card">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#16a34a]" />
                  <CardTitle className="text-base">Shipping Rates — Incl. VAT Total</CardTitle>
                  {currentRates?.baseline_date && currentRates?.updated_at && (
                    <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200 ml-2">
                      Original ({formatShortDate(currentRates.baseline_date)}) vs Current ({formatShortDate(currentRates.updated_at)})
                    </Badge>
                  )}
                  {!currentRates?.baseline_date && currentRates?.updated_at && (
                    <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-100 ml-2">
                      baseline — {formatShortDate(currentRates.updated_at)}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-400 flex items-center gap-1"><Info className="w-3 h-3" /> Hover for full breakdown</span>
                  <span className="text-[10px] text-slate-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Trend @ {selectedWeight}kg</span>
                  {currentRates?.updated_at && (
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(currentRates.updated_at).toLocaleDateString("en-ZA")} {new Date(currentRates.updated_at).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {currentRates?.rates?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="pricing-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-3 py-2.5 text-[10px] font-medium text-slate-500 uppercase sticky left-0 bg-slate-50 z-10 min-w-[160px]">
                          Province
                        </th>
                        {WEIGHT_TIERS.map(t => (
                          <th key={t.label} className="text-center px-2 py-2.5 text-[10px] font-medium text-slate-500 uppercase whitespace-nowrap">
                            {t.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(PROVINCE_PRIMARY_DEST)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([province, dest]) => {
                        const isLocal = province === localProvince;
                        const hasDiscount = isLocal && (wcDiscountLight > 0 || wcDiscountHeavy > 0);
                        const localCities = PROVINCE_CITIES[province] || [];
                        const isAveraged = isLocal && localCities.length > 1;
                        return (
                          <React.Fragment key={province}>
                            {/* FF Cost Row */}
                            <tr className={`hover:bg-slate-50/50 transition-colors ${isLocal ? "bg-orange-50/40" : ""}`} data-testid={`province-row-${province.toLowerCase().replace(/\s+/g, '-')}`}>
                              <td className={`px-3 py-2 sticky left-0 z-10 ${isLocal ? "bg-orange-50/40 border-l-[3px] border-l-[#16a34a]" : "bg-white"}`}>
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PROVINCE_COLORS[province] || "#94a3b8" }} />
                                  <div>
                                    <span className="text-xs font-medium text-slate-900">{province}</span>
                                    {isLocal && <span className="ml-1 text-[9px] text-[#16a34a] font-semibold">(Local)</span>}
                                    {isAveraged && <span className="ml-1 text-[9px] text-amber-600 font-medium">(Avg)</span>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                  <span className="text-[9px] text-slate-400">FF Cost</span>
                                  <Sparkline data={getTrendSeries(dest)} />
                                </div>
                              </td>
                              {WEIGHT_TIERS.map(tier => {
                                const rate = getProvinceRate(province, tier);
                                return renderRawCell(rate, tier);
                              })}
                            </tr>
                            {/* Shopify Price Row */}
                            <tr className={`border-b ${isLocal ? "border-slate-200" : "border-slate-100"} ${hasDiscount ? "bg-green-50/30" : isLocal ? "bg-orange-50/40" : ""}`} data-testid={`province-row-${province.toLowerCase().replace(/\s+/g, '-')}-shopify`}>
                              <td className={`px-3 py-2 sticky left-0 z-10 ${isLocal ? (hasDiscount ? "bg-green-50/30 border-l-[3px] border-l-[#16a34a]" : "bg-orange-50/40 border-l-[3px] border-l-[#16a34a]") : "bg-white"}`}>
                                <span className={`text-[9px] font-medium ml-4 ${hasDiscount ? "text-green-700" : isLocal ? "text-[#16a34a]" : "text-blue-600"}`}>
                                  {hasDiscount ? `Shopify (-${wcDiscountLight}/${wcDiscountHeavy}%)` : "Shopify Price"}
                                </span>
                              </td>
                              {WEIGHT_TIERS.map(tier => {
                                const rate = getProvinceRate(province, tier);
                                return renderTierCell(province, tier, rate);
                              })}
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
                    <p className="text-[10px] text-slate-400">
                      {(wcDiscountLight > 0 || wcDiscountHeavy > 0)
                        ? `${localProvince} discounted: 0-30kg -${wcDiscountLight}%, 30kg+ -${wcDiscountHeavy}%. Other provinces at raw FF rates.`
                        : `Values are Total Incl. VAT. Hover any price for full breakdown. ${localProvince} prices averaged (${(PROVINCE_CITIES[localProvince] || []).join(" + ")}).`
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">No rates stored yet</p>
                  <p className="text-slate-400 text-xs mt-1">Click "Check Prices" to fetch rates for all 25 weight tiers</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chart */}
          <Card className="border-slate-200 shadow-sm mb-6" data-testid="rate-history-chart">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Rate History</CardTitle>
                <Select value={selectedWeight} onValueChange={setSelectedWeight}>
                  <SelectTrigger className="w-[140px]" data-testid="weight-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allWeights.map(w => (
                      <SelectItem key={w} value={String(w)}>{w} kg</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {chartData.length > 1 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date_label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickFormatter={(v) => `R${v}`} />
                    <Tooltip formatter={(value, name) => [`R${Number(value).toFixed(2)}`, name]} contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "11px" }} />
                    <Legend wrapperStyle={{ fontSize: "11px" }} />
                    {destinations.map((dest) => (
                      <Line key={dest} type="monotone" dataKey={dest} stroke={DEST_COLORS[dest] || "#94a3b8"} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12">
                  <TrendingUp className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">Not enough data yet</p>
                  <p className="text-slate-400 text-xs mt-1">Rate checks will build history over time</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Log */}
          <Card className="border-slate-200 shadow-sm" data-testid="rate-change-log">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="text-base">Change Log</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {rateHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">No rate checks recorded yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {[...new Map(rateHistory.map(e => [e.checked_at, e])).values()].map((entry, idx) => (
                    <div key={entry.checked_at || `history-${idx}`} className="px-4 py-3 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${entry.changes_count > 0 ? "bg-amber-100" : "bg-green-100"}`}>
                            {entry.changes_count > 0 ? <AlertCircle className="w-4 h-4 text-amber-600" /> : <CheckCircle className="w-4 h-4 text-green-600" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {new Date(entry.checked_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}
                              <span className="text-xs text-slate-400 ml-2">{new Date(entry.checked_at).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}</span>
                            </p>
                            <p className="text-xs text-slate-500">{entry.rates_checked} rates checked</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.changes_count > 0 ? (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">{entry.changes_count} change(s)</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">No changes</Badge>
                          )}
                          {entry.email_sent && <Mail className="w-4 h-4 text-green-500" />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PricingPage;
